import { memo, useMemo, useState } from 'react'
import { AmendChangeSummary } from '../../utils/appointment-note-addendum.utils'
import { COLLAPSED_LINES } from '../../utils/note-text-diff'
import './styles.scss'

type NoteChangeSummaryProps = {
  changes: AmendChangeSummary
  /** Download links by document id, for the file names in the changes. */
  fileUrls?: Record<string, string>
}

/**
 * The before-and-after block shared by the edit summary step and the edit
 * history, so a change reads identically before and after it is saved.
 */
const NoteChangeSummary: React.FC<NoteChangeSummaryProps> = ({
  changes,
  fileUrls,
}) => {
  const [expanded, setExpanded] = useState(false)
  const { fieldChanges, textDiff } = changes

  // The design renders only what changed; carrying the untouched lines through
  // buries a one-word correction in the rest of the note.
  const changedLines = useMemo(
    () => (textDiff?.lines ?? []).filter((line) => line.isDel || line.isAdd),
    [textDiff]
  )

  // The LCS emits every deletion before every addition, so a plain head slice
  // of a long rewrite showed six struck-through lines and nothing added.
  const visibleLines = useMemo(() => {
    if (expanded || changedLines.length <= COLLAPSED_LINES) {
      return changedLines
    }

    const half = Math.floor(COLLAPSED_LINES / 2)
    const removed = changedLines.filter((line) => line.isDel)
    const added = changedLines.filter((line) => line.isAdd)

    if (removed.length === 0 || added.length === 0) {
      return changedLines.slice(0, COLLAPSED_LINES)
    }

    return [
      ...removed.slice(0, half),
      ...added.slice(0, COLLAPSED_LINES - half),
    ]
  }, [changedLines, expanded])

  return (
    <div className="note-change-summary w-100">
      {fieldChanges.map((change, index) => (
        <p className="op mb-0" key={`${change.label}-${index}`}>
          <span className="op-field">{change.label}</span>
          {change.before !== undefined && (
            <>
              <span className="op-prior">{change.before}</span>
              <span aria-hidden="true" className="op-arrow">
                →
              </span>
            </>
          )}
          <span className="op-next">
            {change.documentId && fileUrls?.[change.documentId] ? (
              <a
                href={fileUrls[change.documentId]}
                target="_blank"
                rel="noreferrer"
              >
                {change.after}
              </a>
            ) : (
              change.after
            )}
          </span>
        </p>
      ))}

      {textDiff && textDiff.wsOnly && (
        <p className="op mb-0">Note text: spacing/formatting changes only</p>
      )}

      {textDiff && !textDiff.wsOnly && (
        <div className="note-text-diff">
          <div className="diff-header d-flex justify-content-between gap-2">
            <span className="op-field">Note text</span>
            <span className="diff-summary">{textDiff.summary}</span>
          </div>
          {textDiff.wordDiff ? (
            <>
              <p className="diff-line mb-0 is-del">
                {'− '}
                {textDiff.wordDiff.removed.map((token, index) => (
                  <span
                    className={token.changed ? 'word-changed' : undefined}
                    key={index}
                  >
                    {token.text}
                  </span>
                ))}
              </p>
              <p className="diff-line mb-0 is-add">
                {'+ '}
                {textDiff.wordDiff.added.map((token, index) => (
                  <span
                    className={token.changed ? 'word-changed' : undefined}
                    key={index}
                  >
                    {token.text}
                  </span>
                ))}
              </p>
            </>
          ) : (
            visibleLines.map((line, index) => (
              <p
                className={`diff-line mb-0 ${line.isDel ? 'is-del' : 'is-add'}`}
                key={index}
              >
                {line.isDel ? '− ' : '+ '}
                {line.text}
              </p>
            ))
          )}
          {!textDiff.wordDiff && changedLines.length > COLLAPSED_LINES && (
            <button
              className="diff-toggle"
              onClick={() => setExpanded((prev) => !prev)}
              type="button"
            >
              {expanded
                ? 'Show fewer lines'
                : `Show all ${changedLines.length} lines`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default memo(NoteChangeSummary)
